import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Loader2, ArrowLeft } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError, maskCpf } from "@/lib/format";
import { api } from "@/lib/api";

export default function Login() {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const isCpf = !loginId.includes("@") && /\d/.test(loginId);

  const forgotPassword = async () => {
    if (!loginId) return toast.error("Informe seu CPF ou e-mail primeiro");
    setForgot(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { login: loginId });
      toast.success(data.message);
    } catch (err) { toast.error(apiError(err)); }
    finally { setForgot(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginId, password);
      toast.success(`Bem-vindo, ${user.name}!`);
      navigate(user.role === "RUNNER" ? "/portal" : "/admin");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-white flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-40" />
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-brand/10 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-neon/10 blur-3xl" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-6" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Voltar ao início
        </Link>
        <div className="rounded-2xl bg-surface border border-white/10 p-8 animate-fade-up">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center p-1"><img src="/logo-cemoc.png" alt="CEMOC RUN" className="w-full h-full object-contain" /></div>
            <div>
              <div className="font-display text-2xl font-black uppercase leading-none">CEMOC RUN</div>
              <div className="text-xs text-slate-400">Acesse sua conta</div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label className="text-slate-300">CPF ou E-mail</Label>
              <Input data-testid="login-identifier" value={loginId}
                onChange={(e) => setLoginId(isCpf ? maskCpf(e.target.value) : e.target.value)}
                className="bg-ink border-white/10 mt-1" placeholder="000.000.000-00 ou email@..." />
              <p className="text-xs text-slate-500 mt-1">Atletas usam CPF. Gestores/Staff usam e-mail.</p>
            </div>
            <div>
              <Label className="text-slate-300">Senha</Label>
              <PasswordInput testId="login-password" value={password}
                onChange={(e) => setPassword(e.target.value)} className="bg-ink border-white/10 mt-1" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={loading}
              className="w-full bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
            </Button>
            <button type="button" data-testid="forgot-password-btn" onClick={forgotPassword} disabled={forgot}
              className="w-full text-sm text-slate-400 hover:text-brand transition-colors">
              {forgot ? "Enviando..." : "Esqueci minha senha"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
